import { useEffect } from 'react';
import { App, Form, Input, InputNumber } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listRates, updateRate, usageQueryKeys } from '../../api/usage';
import './usage.css';

function extractErrorMessage(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message
    ?? (e as Error)?.message
    ?? fallback
  );
}

export function ModelEstimateEditor({ model }: { model: string }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const { data } = useQuery({
    queryKey: usageQueryKeys.rates(model),
    queryFn: () => listRates(model),
  });
  const rate = data?.items[0];

  useEffect(() => {
    if (!rate) return;
    form.setFieldsValue({
      model,
      asrTokensPerSecond: rate.asrTokensPerSecond,
      visionBytesPerToken: rate.visionBytesPerToken,
    });
  }, [form, model, rate]);

  const mut = useMutation({
    mutationFn: updateRate,
    onSuccess: () => {
      message.success('媒体估算已保存');
      void qc.invalidateQueries({ queryKey: usageQueryKeys.rates(model) });
      void qc.invalidateQueries({ queryKey: usageQueryKeys.root });
    },
    onError: (e) => message.error(extractErrorMessage(e, '保存失败')),
  });

  if (!rate) return null;

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card__header"><div className="card__title">媒体估算 · {model}</div></div>
      <div className="card__body">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            model,
            asrTokensPerSecond: rate.asrTokensPerSecond,
            visionBytesPerToken: rate.visionBytesPerToken,
          }}
          onFinish={(v) => mut.mutate({
            model,
            expectedRevision: rate.isFallback ? 0 : rate.revision,
            asrTokensPerSecond: v.asrTokensPerSecond ?? null,
            visionBytesPerToken: v.visionBytesPerToken ?? null,
          })}
        >
          <Form.Item name="model" label="模型">
            <Input disabled />
          </Form.Item>
          <Form.Item name="asrTokensPerSecond" label="ASR tokens/秒">
            <InputNumber stringMode style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="visionBytesPerToken" label="Vision bytes/token">
            <InputNumber stringMode style={{ width: '100%' }} />
          </Form.Item>
          <button type="submit" className="btn btn-primary btn-sm" disabled={mut.isPending}>保存估算</button>
        </Form>
      </div>
    </div>
  );
}
